import { useState, useCallback, useEffect } from 'react';
import Drawer from '../Drawer';
import { fetchCampaigns } from '~/services/campaign';
import { debounce } from 'lodash';

interface Campaign {
    campaign_id: number;
    full_name: string;
    campaign_status: string;
}

interface Props {
    isOpen: boolean;
    setIsOpen: any;
}

const AddCampaignDrawer: React.FC<Props> = ({ isOpen, setIsOpen }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [data, setData] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState(false);
    const [isClient, setIsClient] = useState(false);
    const [isNonClient, setIsNonClient] = useState(false);

    const getCampaign = async (term: string) => {
        setLoading(true);
        const res = await fetchCampaigns({
            full_name: term,
        });
        setData(res.results);
        setLoading(false);
    };

    useEffect(() => {
        // getCampaign('');
    }, []);

    const debouncedFetch = useCallback(
        debounce((term: string) => {
            getCampaign(term);
        }, 500),
        []
    );

    const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newSearchTerm = e.target.value;
        setSearchTerm(newSearchTerm);
        debouncedFetch(newSearchTerm);
    };

    const resetAndCloseForm = () => {
        setIsOpen(false);
        setSearchTerm('');
        setData([]);
    };

    return (
        <Drawer isOpen={isOpen} setIsOpen={setIsOpen}>
            <h2 className='mb-[28px] text-[22px]/[28px] font-bold'>
                Add Campaign
            </h2>
            <div className='mb-4'>
                What type of campaign are you looking to create?{' '}
                <span className='mb-[3px] text-[#F02D3A]'>*</span>
            </div>
            <div className='mb-4'>
                <button
                    className={`m-auto mr-3 h-[30px] justify-self-end rounded-3xl border-[2px] px-[16px] py-[3px] text-[14px]/[18px] font-bold uppercase hover:border-black hover:bg-black hover:text-white ${
                        isClient
                            ? 'border-[#AFC12D] bg-[#AFC12D]'
                            : 'border-black bg-white'
                    }`}
                    onClick={() => {
                        setIsClient(true);
                        setIsNonClient(false);
                    }}
                >
                    Client
                </button>
                <button
                    className={`m-auto mr-3 h-[30px] justify-self-end rounded-3xl border-[2px] px-[16px] py-[3px] text-[14px]/[18px] font-bold uppercase hover:border-black hover:bg-black hover:text-white ${
                        isNonClient
                            ? 'border-[#AFC12D] bg-[#AFC12D]'
                            : 'border-black bg-white'
                    }`}
                    onClick={() => {
                        setIsNonClient(true);
                        setIsClient(false);
                    }}
                >
                    Non-Client
                </button>
            </div>

            <label className='text-[12px]/[14px] font-extrabold'>
                Name{' '}
                <span className='mb-[3px] text-[10px]/[10px] text-[#F02D3A]'>
                    *
                </span>
            </label>
            <div className='mb-[5px] flex rounded-lg border border-[#949599] p-[10px]'>
                <input
                    className='w-full'
                    type='text'
                    value={searchTerm}
                    placeholder='Start typing name'
                    onChange={handleSearchTermChange}
                />
            </div>
            <p className='text-[12px]/[16px]'>
                This is to ensure duplicate Campaigns do not get added.
            </p>

            {loading ? (
                <div className='mt-4 text-center'>
                    <span>Loading...</span>
                </div>
            ) : (
                isClient && (
                    <table className='mt-[25px] w-full'>
                        <thead>
                            <tr className='bg-[#F4F4F5]'>
                                <th className='w-2/3 p-[16px] text-left text-[14px]/[16px] font-normal text-[#131212]'>
                                    Name
                                </th>
                                <th className='w-1/3 p-[16px] text-left text-[14px]/[16px] font-normal text-[#131212]'>
                                    <span className='text-[14px]/[16px]'>
                                        Status
                                    </span>
                                </th>
                                <th className='w-1/3 p-[16px] text-left text-[14px]/[16px] font-normal text-[#131212]'>
                                    <span className='text-[14px]/[16px]'>
                                        Medical Reason
                                    </span>
                                </th>
                                <th className='w-1/3 p-[16px] text-left text-[14px]/[16px] font-normal text-[#131212]'>
                                    <span className='text-[14px]/[16px]'>
                                        Medical Issue
                                    </span>
                                </th>
                                <th className='w-1/3 p-[16px] text-left text-[14px]/[16px] font-normal text-[#131212]'>
                                    <span className='text-[14px]/[16px]'>
                                        DOB
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((campaign) => (
                                <tr key={campaign?.campaign_id}>
                                    <td className='p-[10px]'>
                                        {campaign?.full_name}
                                    </td>
                                    <td className='p-[10px]'>
                                        {campaign?.campaign_status}
                                    </td>
                                    <td className='p-[10px]'>
                                        {campaign?.campaign_type?.name}
                                    </td>
                                    <td className='p-[10px]'>
                                        {
                                            campaign?.patient_info
                                                ?.primary_medical_issue?.name
                                        }
                                    </td>
                                    <td className='p-[10px]'></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )
            )}

            {isNonClient && (
                <div className='mt-4 text-center'>
                    Non-Client Campaigns Here...
                </div>
            )}

            <label className='text-[12px]/[14px] font-extrabold'>
                Email{' '}
                <span className='mb-[3px] text-[10px]/[10px] text-[#F02D3A]'>
                    *
                </span>
            </label>
            <div className='mb-[5px] flex rounded-lg border border-[#949599] p-[10px]'>
                <input
                    className='w-full'
                    type='email'
                    placeholder='email@website.com'
                />
            </div>
            <p className='text-[12px]/[16px]'>
                Email address will also be verified to not be a duplicate of
                another in the system
            </p>

            <div className='mt-[30px]'>
                <button
                    className='m-auto mr-3 justify-self-end rounded-3xl border-[2px] border-black bg-white px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase hover:bg-black hover:text-white'
                    onClick={resetAndCloseForm}
                >
                    Cancel
                </button>
                {Array.isArray(data) && data.length && loading ? (
                    <button
                        disabled
                        className='m-auto mr-3 rounded-full bg-[#F4F4F5] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-[#949599] hover:bg-opacity-90'
                    >
                        Continue
                    </button>
                ) : (
                    <button
                        className='m-auto mr-3 rounded-full bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-[#131212] hover:bg-opacity-90'
                        onClick={resetAndCloseForm}
                    >
                        Continue
                    </button>
                )}
            </div>
        </Drawer>
    );
};

export default AddCampaignDrawer;
